import * as React from 'react';
import type { AuthCheckErrorKind } from './AuthContext';

// Fixed copy per kind — the only place authCheckError's kind is turned into
// user-facing text. Keeping this mapping here (not inline in LoginPage) is
// what makes AuthContext.tsx's closed `AuthCheckErrorKind` union actually
// enforce "never show a raw backend message": a page can't accidentally pass
// `err.message` through, because there's nothing to pass — only a kind.
const AUTH_CHECK_ERROR_MESSAGES: Record<AuthCheckErrorKind, string> = {
  unavailable: 'Couldn’t verify your account right now. Please try again.',
  network: 'Network error while signing in. Check your connection and try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  google_link_not_approved:
    'Please sign in with your password, then link Google from Settings → Account.',
  account_not_found: 'No account was found for this sign-in. Please contact support.',
  account_inactive: 'Your account is no longer active. Please contact support.',
};

/**
 * Shared between the admin and brand LoginPage — both need the identical
 * reaction to AuthContext's two async-rejection signals: a cross-app 403
 * (`crossAppDenied`, shown via the page's own generic anti-enumeration
 * message) and a non-403 authorization-check failure (`authCheckError`,
 * shown via the kind-to-copy map above, since it reveals nothing about
 * credential validity and is safe to show as-is).
 */
export function useLoginErrorSync({
  crossAppDenied,
  authCheckError,
  genericMessage,
  setError,
  setIsLoading,
}: {
  crossAppDenied: boolean;
  authCheckError: AuthCheckErrorKind | null;
  genericMessage: string;
  setError: (message: string | null) => void;
  setIsLoading: (loading: boolean) => void;
}): void {
  React.useEffect(() => {
    if (crossAppDenied) {
      setError(genericMessage);
      setIsLoading(false);
    }
  }, [crossAppDenied, genericMessage, setError, setIsLoading]);

  React.useEffect(() => {
    if (authCheckError) {
      setError(AUTH_CHECK_ERROR_MESSAGES[authCheckError]);
      setIsLoading(false);
    }
  }, [authCheckError, setError, setIsLoading]);
}
